import React from 'react';

// Collapse each row into horizontal runs so a sprite renders as a handful of rects.
export const spriteRuns = (rows, palette) => {
  const runs = [];

  rows.forEach((row, y) => {
    let x = 0;
    while (x < row.length) {
      const key = row[x];
      let end = x + 1;
      while (end < row.length && row[end] === key) end += 1;
      if (palette[key]) {
        runs.push({ x, y, width: end - x, fill: palette[key] });
      }
      x = end;
    }
  });

  return runs;
};

export const PixelRuns = ({ runs, offsetX = 0, offsetY = 0, className }) => (
  <g className={className}>
    {runs.map(({ x, y, width, fill }) => (
      <rect
        key={`${x}-${y}`}
        x={x + offsetX}
        y={y + offsetY}
        width={width}
        height="1"
        style={{ fill }}
      />
    ))}
  </g>
);

const PixelSprite = ({ rows, palette, className }) => {
  const width = Math.max(...rows.map((row) => row.length));

  return (
    <svg
      className={className}
      viewBox={`0 0 ${width} ${rows.length}`}
      shapeRendering="crispEdges"
      aria-hidden="true"
      focusable="false"
    >
      <PixelRuns runs={spriteRuns(rows, palette)} />
    </svg>
  );
};

// The logo renders the block and its shadow as separate groups so the block can lift on hover.
export const LogoMark = ({ className }) => {
  const { rows, palette } = sprites.logo;
  const runs = spriteRuns(rows, palette);
  const isShadow = (run) => run.fill === palette.s;

  return (
    <svg
      className={className}
      viewBox={`-0.5 0 ${rows[0].length + 1} ${rows.length}`}
      shapeRendering="crispEdges"
      aria-hidden="true"
      focusable="false"
    >
      <PixelRuns className="logo-shadow" runs={runs.filter(isShadow)} />
      <PixelRuns className="logo-block" runs={runs.filter((run) => !isShadow(run))} />
    </svg>
  );
};

const ink = 'var(--sprite-ink)';

export const sprites = {
  // A block floating above its own shadow: very little gravity.
  logo: {
    palette: { k: 'var(--logo-ink)', y: 'var(--yellow)', w: '#fff6d6', o: 'var(--logo-shade)', s: 'var(--logo-shadow)' },
    rows: [
      '..kkkkkkk..',
      '.kyyyyyyyk.',
      '.kywwyyyyk.',
      '.kywyyyyyk.',
      '.kyyyyyyyk.',
      '.kyyyyyyok.',
      '.kyyyyyook.',
      '..kkkkkkk..',
      '...........',
      '...........',
      '...........',
      '..s.s.s.s..',
      '...s.s.s...'
    ]
  },
  sun: {
    palette: { k: ink, y: 'var(--yellow)' },
    rows: [
      '....k....',
      '.k.....k.',
      '...kkk...',
      '..kyyyk..',
      'k.kyyyk.k',
      '..kyyyk..',
      '...kkk...',
      '.k.....k.',
      '....k....'
    ]
  },
  moon: {
    palette: { k: ink, y: 'var(--yellow)' },
    rows: [
      '...kkk...',
      '..kyyk...',
      '.kyyk....',
      '.kyyk....',
      '.kyyk....',
      '.kyyyk..k',
      '..kyyykk.',
      '...kkk...',
      '.........'
    ]
  },
  beacon: {
    palette: { k: ink, w: 'var(--sprite-wave)', l: 'var(--sprite-light)' },
    rows: [
      '............',
      '.w........w.',
      'w..w....w..w',
      'w.w..kk..w.w',
      'w.w.kllk.w.w',
      'w..w.kk.w..w',
      '.w...kk...w.',
      '.....kk.....',
      '....kkkk....',
      '...k.kk.k...',
      '..k..kk..k..',
      '.kkkkkkkkkk.'
    ]
  },
  flask: {
    palette: { k: ink, g: 'var(--sprite-liquid)', r: 'var(--sprite-heart)' },
    rows: [
      '...kkkkkk...',
      '....k..k....',
      '....k..k....',
      '....k..k....',
      '...k....k...',
      '..k......k..',
      '.kggggggggk.',
      '.kggrgrgggk.',
      '.kgrrrrrggk.',
      '.kggrrrgggk.',
      '.kgggrggggk.',
      '..kkkkkkkk..'
    ]
  },
  hourglass: {
    palette: { k: ink, s: 'var(--sprite-sand)' },
    rows: [
      'kkkkkkkkkkkk',
      '.k........k.',
      '.kssssssssk.',
      '..kssssssk..',
      '...kssssk...',
      '....kssk....',
      '....k.sk....',
      '...k..s.k...',
      '..k...s..k..',
      '.k...sss..k.',
      '.k.ssssss.k.',
      'kkkkkkkkkkkk'
    ]
  }
};

export default PixelSprite;
